// src/components/admin/VivaakOfferingAdmin.js

import React, { useState, useRef } from "react";
import { uploadFile } from "../../../config/uploadFile";
import { useData } from "../../../context/DataContext";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";
import { MultiImageUploadField } from "../../../utils/ImageUploadField";

const SLOT_LABELS = [
  "Slot 1 — Left Top (Large Card)",
  "Slot 2 — Left Bottom Left (Small)",
  "Slot 3 — Left Bottom Right (Small)",
  "Slot 4 — Right Top (Tall Card)",
  "Slot 5 — Right Bottom",
];

export default function VivaakOfferingAdmin() {
  const { vivaakOfferings, postVivaakOfferings } = useData();

  const [title, setTitle] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const dragIndexRef = useRef(null);

  // ── Drag-and-drop handlers ──────────────────────────────────
  const handleDragStart = (idx) => {
    dragIndexRef.current = idx;
  };

  const handleDragEnter = (idx) => {
    setDragOverIndex(idx);
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // required to allow drop
  };

  const handleDrop = async (dropIdx) => {
    const dragIdx = dragIndexRef.current;
    if (dragIdx === null || dragIdx === dropIdx) {
      setDragOverIndex(null);
      return;
    }
    const updated = [...vivaakOfferings];
    const [dragged] = updated.splice(dragIdx, 1);
    updated.splice(dropIdx, 0, dragged);
    dragIndexRef.current = null;
    setDragOverIndex(null);
    await postVivaakOfferings(updated);
    toast.success(`"${dragged.title}" moved to slot ${dropIdx + 1}`);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  // ── File selection ──────────────────────────────────────────
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  // ── Save / Update offering ──────────────────────────────────
  const handleSaveOffering = async () => {
    if (!title.trim()) {
      toast.error("Title is required.");
      return;
    }

    let finalUrls = [...uploadedUrls];

    if (selectedFiles.length > 0) {
      setIsUploading(true);
      toast.loading("Uploading images...", { id: "upload" });
      try {
        const newUrls = [];
        for (let file of selectedFiles) {
          const url = await uploadFile(file);
          newUrls.push(url);
        }
        finalUrls = [...finalUrls, ...newUrls];
        setSelectedFiles([]);
        setUploadedUrls(finalUrls);
        toast.success("Images uploaded!", { id: "upload" });
      } catch {
        toast.error("Upload failed", { id: "upload" });
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    if (finalUrls.length === 0) {
      toast.error("Please select at least one image.");
      return;
    }

    const updatedOffering = { title: title.trim(), images: finalUrls };
    const updatedOfferings = [...vivaakOfferings];

    if (editIndex !== null) {
      updatedOfferings[editIndex] = updatedOffering;
      toast.success("Offering updated successfully!");
    } else {
      updatedOfferings.push(updatedOffering);
      toast.success("New offering created!");
    }

    await postVivaakOfferings(updatedOfferings);
    setTitle("");
    setUploadedUrls([]);
    setEditIndex(null);
  };

  // ── Edit ────────────────────────────────────────────────────
  const handleEditOffering = (index) => {
    const offering = vivaakOfferings[index];
    setTitle(offering.title);
    setUploadedUrls(offering.images || []);
    setEditIndex(index);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Remove single image ─────────────────────────────────────
  const handleRemoveSingleImage = (imgIndex) => {
    setUploadedUrls((prev) => prev.filter((_, i) => i !== imgIndex));
  };

  // ── Delete offering ─────────────────────────────────────────
  const handleDeleteOffering = async (index) => {
    const result = await Swal.fire({
      title: "Delete Entire Offering?",
      text: "This will remove the title and all associated images. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete everything!",
    });

    if (!result.isConfirmed) return;

    try {
      const updatedOfferings = [...vivaakOfferings];
      updatedOfferings.splice(index, 1);
      await postVivaakOfferings(updatedOfferings);
      toast.success("Offering deleted!");
      if (editIndex === index) {
        setEditIndex(null);
        setTitle("");
        setUploadedUrls([]);
      }
    } catch {
      toast.error("Failed to delete");
    }
  };

  // ── Render ──────────────────────────────────────────────────
  return (
    <>
    <div className="p-4 bg-white rounded shadow">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800">
        {editIndex !== null
          ? "Edit Vivaak Offering"
          : "Add New Vivaak Offering"}
      </h2>

      {/* Title input */}
      <div className="mb-6">
        <label className="block text-sm font-bold text-gray-600 mb-2 uppercase tracking-wide">
          Offering Title
        </label>
        <input
          className="border-2 border-gray-100 rounded-xl p-3 w-full focus:border-gray-900 outline-none transition-colors shadow-sm"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Indoor Pool, Garden View..."
        />
      </div>

      {/* Image picker */}
      <div className="mb-4">
        <MultiImageUploadField
          id="offeringImages"
          label="Offering Images"
          hint="Recommended size: 600×400px. You can select multiple images."
          required
          existingUrls={uploadedUrls}
          newFiles={selectedFiles}
          onAdd={(files) => setSelectedFiles((prev) => [...prev, ...files])}
          onRemove={(idx, isExisting) => {
            if (isExisting) {
              setUploadedUrls((prev) => prev.filter((_, i) => i !== idx));
            } else {
              setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
            }
          }}
        />
      </div>

      {/* Uploaded images list */}
      {uploadedUrls.length > 0 && (
        <div className="mb-8 overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
          <div className="bg-gray-800 px-4 py-3 flex justify-between items-center text-white">
            <h4 className="font-bold text-xs uppercase tracking-widest">
              Active Images ({uploadedUrls.length})
            </h4>
            <button
              onClick={() => setUploadedUrls([])}
              className="text-xs bg-red-500/20 hover:bg-red-500 px-2 py-1 rounded transition-colors"
            >
              Remove All
            </button>
          </div>
          <div className="max-h-[300px] overflow-y-auto bg-white">
            {uploadedUrls.map((url, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 border-b border-gray-50 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <span className="text-xs font-mono text-gray-300 w-4">
                    {idx + 1}
                  </span>
                  <div className="w-16 h-12 bg-gray-100 rounded overflow-hidden shadow-inner">
                    <img
                      src={url}
                      alt="Offering"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-xs text-gray-600 font-medium truncate max-w-[200px]">
                    {url.split("/").pop().split("?")[0]}
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveSingleImage(idx)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save / Cancel */}
      <div className="flex gap-4">
        <button
          className="bg-gray-900 hover:bg-black text-white px-10 py-3.5 rounded-xl mb-4 font-bold transition-all flex-1 md:flex-none uppercase tracking-wider text-sm disabled:bg-gray-300"
          onClick={handleSaveOffering}
          disabled={
            isUploading ||
            !title.trim() ||
            (uploadedUrls.length === 0 && selectedFiles.length === 0)
          }
        >
          {isUploading
            ? "Processing..."
            : editIndex !== null
              ? "Update Offering"
              : "Save Offering"}
        </button>

        {editIndex !== null && (
          <button
            className="bg-white border-2 border-gray-100 text-gray-400 px-6 py-3 rounded-xl mb-4 font-bold hover:bg-gray-50 transition-all uppercase tracking-wider text-sm"
            onClick={() => {
              setEditIndex(null);
              setTitle("");
              setUploadedUrls([]);
              setSelectedFiles([]);
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {/* ── Catalog ─────────────────────────────────────────── */}
      <div className="mt-12">
        <div className="flex items-center gap-4 mb-2">
          <h3 className="text-xl font-black text-gray-800 uppercase tracking-tighter">
            Your Catalog
          </h3>
          <div className="h-px bg-gray-100 flex-1" />
        </div>

        <p className="text-xs text-gray-400 mb-6">
          Drag &amp; drop cards below to reorder them.
        </p>
        </div>

        {vivaakOfferings.length === 0 && (
          <p className="text-gray-400 italic">Catalog is empty...</p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {vivaakOfferings.map((off, idx) => (
            <div
              key={idx}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragEnter={() => handleDragEnter(idx)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(idx)}
              onDragEnd={handleDragEnd}
              className={`relative bg-white border rounded-3xl overflow-hidden shadow-sm transition-all duration-200 group ${
                dragOverIndex === idx
                  ? "border-gray-900 shadow-lg scale-[1.02] bg-gray-50"
                  : "border-gray-100 hover:shadow-xl"
              }`}
            >
              {/* Drag handle + slot badge */}
              <div className="px-4 pt-3 pb-0 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {/* Drag grip icon */}
                  <div className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-700 transition-colors select-none">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <circle cx="7" cy="5" r="1.4" />
                      <circle cx="13" cy="5" r="1.4" />
                      <circle cx="7" cy="10" r="1.4" />
                      <circle cx="13" cy="10" r="1.4" />
                      <circle cx="7" cy="15" r="1.4" />
                      <circle cx="13" cy="15" r="1.4" />
                    </svg>
                  </div>
                  <span className="bg-gray-900 text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter">
                    #{idx + 1}
                  </span>
                  <span className="text-[10px] text-gray-400 font-semibold truncate max-w-[140px]">
                    {SLOT_LABELS[idx] || `Slot ${idx + 1}`}
                  </span>
                </div>
              </div>

              {/* Card body */}
              <div className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-extrabold text-gray-700 truncate pr-4 text-base">
                    {off.title}
                  </h4>
                  <span className="bg-gray-800 text-white text-[9px] font-black px-2 py-1 rounded-full uppercase tracking-tighter shadow-sm whitespace-nowrap">
                    {off.images?.length || 0} Photos
                  </span>
                </div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-gray-50 border border-gray-100 relative shadow-inner">
                  <img
                    src={off.images?.[0]}
                    alt={off.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    draggable={false}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>

              {/* Action row */}
              <div className="flex border-t border-gray-100">
                <button
                  onClick={() => handleEditOffering(idx)}
                  className="flex-1 py-3 text-gray-700 font-bold text-xs uppercase tracking-widest hover:bg-gray-50 transition-colors"
                >
                  Edit
                </button>
                <div className="w-px bg-gray-100" />
                <button
                  onClick={() => handleDeleteOffering(idx)}
                  className="flex-1 py-3 text-red-400 font-bold text-xs uppercase tracking-widest hover:bg-red-50 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
    </div>
    </>
  );
}
